import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from 'modules/state-mgmt/store';
import {
  confirmResetPassword as confirmResetPasswordAction,
  validateResetPasswordToken as validateResetPasswordTokenAction,
} from 'modules/state-mgmt/user';
import { prettifyErrorResponse } from 'utils';
import { ROUTES } from 'constants/routes';

interface IUseConfirmResetPassword {
  onSuccess?: () => void;
  onFailure?: (backendErrors: any) => void;
}

interface IUseValidateResetPasswordToken {
  onSuccess?: () => void;
  onFailure?: () => void;
}

export const useConfirmResetPassword = ({ onSuccess, onFailure }: IUseConfirmResetPassword) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const confirmResetPassword = async (
    email: string | null,
    mobilePhoneNumber: string | null,
    token: string,
    password: string,
  ) => {
    setIsLoading(true);

    try {
      const payload: any = {
        token,
        password,
      };
      if (email) payload.email = email;
      if (mobilePhoneNumber) payload.mobilePhoneNumber = mobilePhoneNumber;

      await dispatch(confirmResetPasswordAction(payload)).unwrap();

      setIsLoading(false);
      enqueueSnackbar(t('confirmResetPassword.successMessage'), { variant: 'success' });
      onSuccess?.();
      navigate(ROUTES.LOGIN.path);
    } catch (error) {
      setIsLoading(false);
      onFailure?.(error);
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  return { isLoading, confirmResetPassword };
};

export const useValidateResetPasswordToken = ({ onSuccess, onFailure }: IUseValidateResetPasswordToken) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const validateToken = useCallback(
    async (email: string | null, mobilePhoneNumber: string | null, token: string) => {
      setIsLoading(true);

      try {
        const payload: any = { token };
        if (email) payload.email = email;
        if (mobilePhoneNumber) payload.mobilePhoneNumber = mobilePhoneNumber;

        const response = await dispatch(validateResetPasswordTokenAction(payload)).unwrap();

        setIsLoading(false);
        if (response === true) {
          onSuccess?.();
        } else {
          onFailure?.();
        }
      } catch (error) {
        setIsLoading(false);
        onFailure?.();
      }
    },
    [dispatch, onSuccess, onFailure],
  );

  return { isLoading, validateToken };
};
